/**
 * Shared, pure (no-JSX) helpers for the tool-use cards.
 *
 * The exported functions here have external consumers and dedicated tests
 * (`extractFileInfo`/`extractBashOutputFiles` → MessageItem; `parseSearchResult`
 * → WebSearchCard + test). Their original import path (`../ToolUseBlock`) is
 * preserved via re-exports from ToolUseBlock.tsx, so no consumer changes.
 */

import { isBinaryFile } from "@/lib/file-utils";

// Tools that create/modify files - auto-expand these
export const FILE_TOOLS = ["Write", "Edit", "NotebookEdit"];

// Map file extensions to language names for display
const EXTENSION_TO_LANGUAGE: Record<string, string> = {
  js: "JavaScript",
  jsx: "JavaScript",
  ts: "TypeScript",
  tsx: "TypeScript",
  py: "Python",
  rb: "Ruby",
  go: "Go",
  rs: "Rust",
  java: "Java",
  kt: "Kotlin",
  swift: "Swift",
  c: "C",
  cpp: "C++",
  h: "C Header",
  cs: "C#",
  php: "PHP",
  html: "HTML",
  css: "CSS",
  scss: "SCSS",
  json: "JSON",
  yaml: "YAML",
  yml: "YAML",
  xml: "XML",
  md: "Markdown",
  sql: "SQL",
  sh: "Shell",
  bash: "Bash",
  zsh: "Zsh",
  dockerfile: "Dockerfile",
  toml: "TOML",
  ini: "INI",
  env: "Environment",
  txt: "Text",
};

// Extract toolkit name from tool name (e.g., "GITHUB_CREATE_ISSUE" -> "github")
export function extractToolkitFromToolName(toolName: string): string | null {
  const parts = toolName.split("_");
  if (parts.length > 1) {
    return parts[0].toLowerCase();
  }
  return null;
}

// Strip sandbox-internal path prefixes that are meaningless to users
export function humanizePath(filePath: string): string {
  return filePath
    .replace(/^\/app\/workspace\//, "")
    .replace(/^\/home\/[^/]+\//, "~/");
}

// Extract file info from Write/Edit tool input
export function extractFileInfo(
  toolName: string,
  toolInput: Record<string, unknown>
): {
  filePath: string | null;
  content: string | null;
  language: string;
  fileName: string;
  ext: string;
  isBinary: boolean;
} {
  let filePath: string | null = null;
  let content: string | null = null;

  if (toolName === "Write") {
    filePath =
      (toolInput.file_path as string) || (toolInput.filePath as string) || null;
    content = (toolInput.content as string) || null;
  } else if (toolName === "Edit") {
    filePath =
      (toolInput.file_path as string) || (toolInput.filePath as string) || null;
    content =
      (toolInput.new_string as string) ||
      (toolInput.newString as string) ||
      null;
  } else if (toolName === "NotebookEdit") {
    filePath =
      (toolInput.notebook_path as string) ||
      (toolInput.notebookPath as string) ||
      null;
    content =
      (toolInput.new_source as string) ||
      (toolInput.newSource as string) ||
      null;
  }

  const fileName = filePath ? filePath.split("/").pop() || filePath : "unknown";
  const ext = fileName.includes(".")
    ? fileName.split(".").pop()?.toLowerCase() || ""
    : "";
  const language = EXTENSION_TO_LANGUAGE[ext] || "Plain Text";
  const binary = filePath ? isBinaryFile(filePath) : false;

  return { filePath, content, language, fileName, ext, isBinary: binary };
}

// Extract output file paths from Bash tool command and result
export function extractBashOutputFiles(
  toolInput: Record<string, unknown>,
  result?: string
): string[] {
  const command = (toolInput.command as string) || "";
  const text = `${command} ${result || ""}`;
  const paths = new Set<string>();

  // Scoped to the user's deliverables dir (files/) — the SAME directory the
  // server's output watcher treats as task outputs. This keeps the shelf
  // aligned with the Computer "This task" panel and excludes a skill's OWN
  // reference/example files (read under .claude/abilities/…).
  //
  // Match BOTH an absolute sandbox path (/app/workspace/files/…) AND a RELATIVE
  // path (files/…): the agent commonly writes deliverables via Bash —
  // `cat > files/report.html` or `register-output … "files/report.html"` —
  // rather than the Write tool, and uses a relative path. A required boundary
  // before `files/` (start, whitespace, quote, =, >, (, [, backtick) prevents
  // matching "profiles/"/"myfiles/". Scaffolding (.claude/…, examples/…) is
  // excluded by construction — it never sits under files/. The push() gate
  // canonicalizes + re-checks the files/ scope for every source.
  const regex =
    /(?:^|[\s"'(\[=>`])((?:\/app\/workspace\/)?files\/[^\s"')\]`]+?\.(?:pdf|pptx|docx|xlsx|png|jpg|jpeg|gif|svg|webp|mp4|webm|mov|mp3|wav|ogg|m4a|zip|html|htm|md|csv|txt|json))/gi;
  let m: RegExpExecArray | null;
  while ((m = regex.exec(text)) !== null) {
    paths.add(m[1].replace(/[),;'"\\]+$/, ""));
  }

  return Array.from(paths);
}

/** Extract a clean domain + short path from a URL. */
export function formatUrl(url: string): { display: string; full: string } {
  try {
    const u = new URL(url);
    const domain = u.hostname.replace(/^www\./, "");
    const path = u.pathname === "/" ? "" : u.pathname;
    const display =
      domain + (path.length > 35 ? path.slice(0, 35) + "..." : path);
    return { display, full: url };
  } catch {
    return {
      display: url.length > 50 ? url.slice(0, 50) + "..." : url,
      full: url,
    };
  }
}

/** Try to extract source URLs from a WebSearch result string. */
function extractSearchSources(
  result: unknown
): { title: string; url: string; domain: string }[] {
  if (!result) return [];
  const text = typeof result === "string" ? result : JSON.stringify(result);
  const sources: { title: string; url: string; domain: string }[] = [];
  const seenDomains = new Set<string>();
  // Match markdown-style links: [Title](url)
  const linkRegex = /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g;
  let match;
  while ((match = linkRegex.exec(text)) !== null && sources.length < 6) {
    try {
      const domain = new URL(match[2]).hostname.replace(/^www\./, "");
      if (!seenDomains.has(domain)) {
        seenDomains.add(domain);
        sources.push({ title: match[1].slice(0, 80), url: match[2], domain });
      }
    } catch {
      /* skip malformed */
    }
  }
  // Fallback: bare URLs if no markdown links found
  if (sources.length === 0) {
    const urlRegex = /https?:\/\/[^\s"')<>]+/g;
    let urlMatch;
    while ((urlMatch = urlRegex.exec(text)) !== null && sources.length < 6) {
      try {
        const u = new URL(urlMatch[0]);
        const domain = u.hostname.replace(/^www\./, "");
        if (!seenDomains.has(domain)) {
          seenDomains.add(domain);
          const path = u.pathname.replace(/\/$/, "").split("/").pop() || "";
          const title = path
            ? path.replace(/[-_]/g, " ").replace(/\.\w+$/, "")
            : domain;
          sources.push({ title, url: urlMatch[0], domain });
        }
      } catch {
        /* skip */
      }
    }
  }
  return sources;
}

// The untrusted-content fence the dispatcher mints around a third-party tool
// result. The markers are FROZEN in the SDK (packages/agent-sdk/src/tools/
// tool-types.ts) because they are journaled; duplicated here as literals rather
// than imported because apps/web does not depend on the SDK package and must not
// pull it into the browser bundle for two strings.
const UNTRUSTED_FENCE_OPEN = "<untrusted_external_content>\n";
const UNTRUSTED_FENCE_CLOSE = "\n</untrusted_external_content>";

/**
 * Unwrap a FULLY-framed untrusted result; anything else returns unchanged.
 * Exact inverse of the SDK's `fenceUntrustedText`, and deliberately as strict:
 * the cards use this only to reach the payload they used to parse, so a partial
 * or near-miss frame must fall through untouched rather than be guessed at.
 *
 * Without it, every fenced WebSearch/WebFetch result fails `JSON.parse` and the
 * cards silently lose their structured detail (search excerpts and duration,
 * fetch status and size).
 */
export function stripUntrustedFence(text: string): string {
  if (
    text.startsWith(UNTRUSTED_FENCE_OPEN) &&
    text.endsWith(UNTRUSTED_FENCE_CLOSE) &&
    text.length >= UNTRUSTED_FENCE_OPEN.length + UNTRUSTED_FENCE_CLOSE.length
  ) {
    return text.slice(
      UNTRUSTED_FENCE_OPEN.length,
      text.length - UNTRUSTED_FENCE_CLOSE.length
    );
  }
  return text;
}

/**
 * The fenced body of a result that STARTS with the fence, plus whatever the harness
 * appended after the close marker (an mcp_invoke result's async-job note rides there).
 * Exact: the SDK sanitizer rewrites every copy of the marker inside the content, so the
 * first close marker ends the body. Unfenced or unclosed text is all body.
 */
export function splitUntrustedFence(text: string): { body: string; rest: string } {
  const end = text.startsWith(UNTRUSTED_FENCE_OPEN)
    ? text.indexOf(UNTRUSTED_FENCE_CLOSE, UNTRUSTED_FENCE_OPEN.length)
    : -1;
  if (end < 0) return { body: text, rest: "" };
  return {
    body: text.slice(UNTRUSTED_FENCE_OPEN.length, end),
    rest: text.slice(end + UNTRUSTED_FENCE_CLOSE.length),
  };
}

/** Parse the SDK's structured WebSearchOutput, falling back to regex extraction. */
export function parseSearchResult(result: unknown): {
  sources: { title: string; url: string; domain: string; excerpt?: string }[];
  durationSec: number | null;
} {
  if (!result) return { sources: [], durationSec: null };
  const text = stripUntrustedFence(
    typeof result === "string" ? result : JSON.stringify(result)
  );

  try {
    const parsed = JSON.parse(text) as {
      results?: unknown[];
      durationSeconds?: number;
    };
    if (parsed && Array.isArray(parsed.results)) {
      const sources: {
        title: string;
        url: string;
        domain: string;
        excerpt?: string;
      }[] = [];
      const seen = new Set<string>();
      for (const r of parsed.results) {
        if (!r || typeof r === "string") continue;
        const content = (r as { content?: unknown }).content;
        if (!Array.isArray(content)) continue;
        const excerpts = (r as { excerpts?: unknown }).excerpts;
        const excerpt =
          Array.isArray(excerpts) && typeof excerpts[0] === "string"
            ? (excerpts[0] as string).slice(0, 160)
            : undefined;
        for (const item of content) {
          const i = item as { title?: unknown; url?: unknown };
          if (typeof i?.url !== "string" || typeof i?.title !== "string")
            continue;
          try {
            const domain = new URL(i.url).hostname.replace(/^www\./, "");
            if (seen.has(domain)) continue;
            seen.add(domain);
            sources.push({
              title: i.title.slice(0, 80),
              url: i.url,
              domain,
              excerpt,
            });
            if (sources.length >= 6) break;
          } catch {
            /* skip malformed */
          }
        }
        if (sources.length >= 6) break;
      }
      const durationSec =
        typeof parsed.durationSeconds === "number"
          ? parsed.durationSeconds
          : null;
      if (sources.length > 0) return { sources, durationSec };
    }
  } catch {
    /* not JSON — fall through */
  }

  return { sources: extractSearchSources(text), durationSec: null };
}
